/**
 * aiChatHistory · 拉某 conversation 的历史消息序列
 *
 * 协议对齐 (2026-05-25 改造 · 阶段 1 + B 路径清理):
 *   POST /web/workgroup/folder/ai/chat/history
 *   body: { group_id, conversation_id, limit?, before_id? }
 *   resp: {
 *     conversation_id,
 *     messages: [ { id, message_id, ..., data: <4 种 type 之一> }, ... ],
 *     has_more,
 *     next_before_id
 *   }
 *
 * 一行 ai_chat_message = 一轮交互 (user_query + 一组 AI 输出)。
 * 前端拆成 user TextMessage + assistant AssistantMessage 两条。
 *
 * 协议约定 (文档 FAQ Q2): data == null 的消息 (预览操作占位 / 建夹完成回执 /
 * 迁移前老消息) 业务上不展示, **由调用方 pre-filter 掉**, transformBackendMessage
 * 强假设 m.data 非空。
 *
 * 4 种 data.type 分流:
 *   · text         · 纯文本气泡
 *   · multi_select · 选择卡 (映射到 next_level)
 *   · folder       · 文件夹方案 (folders 留空数组占位, 阶段 2 改成调接口 5 拉树)
 *   · task         · 建夹任务回执 (阶段 1 暂只渲染 text + content, card 阶段 4 处理)
 */

import type {
  AssistantCard,
  AssistantMessage,
  AssistantPayload,
  ChoiceOption,
  TextMessage,
  ToolCallRecord
} from '../../../types/ai-classify'
import { summarizeArgs, summarizeResult } from './toolDisplayMap'

declare const __API_SERVER_HOST__: string

// ============================================================
// 协议层: 请求 / 响应类型 (对齐接口 4 文档)
// ============================================================

interface HistoryRequest {
  conversation_id: string
  /** webxcameraapi BFF 强校验所有 ai/chat/* 路由必须含 group_id (UUID 形式) */
  group_id: string
  /** 默认 20, 上限 100; 阶段 1 调用方暂传 100 一次拉够多, 分页阶段 4 引入 */
  limit?: number
  /** 翻页游标 · 0 = 拉最新一批; > 0 = 上批 next_before_id, 拉更早一批 */
  before_id?: number
}

/**
 * 接口 4 子对象 · 4 种 data.type union (服务端真实结构)
 *
 * 接口 4 message 顶层包络都一致 (id / user_query / status 等), 仅 data 字段
 * 按 LLM 输出形态 4 选 1。前端按 data.type 分流渲染。
 */
type BackendHistoryMessageData =
  | BackendHistoryDataText
  | BackendHistoryDataMultiSelect
  | BackendHistoryDataFolder
  | BackendHistoryDataTask

/** 4.D 纯文本 / 反问 / 失败回执 / 兜底 */
interface BackendHistoryDataText {
  type: 'text'
  content: string
}

/** 4.C LLM 选择卡 */
interface BackendHistoryDataMultiSelect {
  type: 'multi_select'
  content: string
  /** 固定 true, 标识"这是选择卡" */
  is_selectable: boolean
  /**
   * 字段缺失 = 最新一条可继续点选;
   * true = 不是会话最新一条 → 历史回看, 选项只读不可点
   *
   * 阶段 1 暂不消费本字段 (前端可从消息列表态自己判断"是不是最后一条"),
   * 等渲染层做"灰化"时再 wire 进 AssistantPayload。
   */
  disabled?: boolean
  interaction_state: {
    /** 当前固定 'options' */
    scope: string
    /**
     * 选项数组 · key = 选项 id, name = 主标题, detail = 副标题 (可选)
     * detail 渲染在选项 name 下方做说明文案 (如 "如\"时间只看最近 3 个月\"")
     */
    options: Array<{ key: string; name: string; detail?: string }>
  }
}

/** 4.A LLM 文件夹方案 (folders / excluded_keys 都在接口 5 一次返) */
interface BackendHistoryDataFolder {
  type: 'folder'
  content: string
}

/** 4.B 建夹任务回执 + 实时进度 */
interface BackendHistoryDataTask {
  type: 'task'
  content: string
  /** 服务端拉 history 时实时查 build_task 表填充, 前端不用再调接口 1 */
  card?: Record<string, unknown>
  /** 当时方案的完整文件夹树 (PreviewFolder 结构, 全嵌套) */
  top_folders?: unknown[]
}

/** 后端 ai_chat_message 一行 (json tag 严格匹配 model/chat.go) */
export interface BackendChatMessage {
  /** DB 主键, 兼容字段 (旧 schema) */
  id: number
  /** = id 业务命名, 推荐用本字段; 翻页游标 / 接口 5 入参都用它 */
  message_id?: number
  conversation_id: string
  group_id?: string
  user_query: string
  /** json 类型字段, 前端拿到的是已解析的对象数组 (不是字符串) */
  mcp_tool_calls?: BackendToolCall[] | null
  classify_state: string
  session_id?: string
  trace_id?: string
  status: number
  /** 0 = 未表态 / 1 = 赞 / 2 = 踩, 阶段 1 暂不消费 */
  feedback?: number
  created_at: string
  /**
   * 核心结构化 payload, 按 data.type 渲染。
   *
   * data == null 表示预览操作占位 / 建夹完成回执 / 迁移前老消息, 业务约定
   * 跳过渲染 (文档 FAQ Q2)。**调用方负责 pre-filter, transformBackendMessage
   * 强假设非空。**
   */
  data?: BackendHistoryMessageData | null
}

/** 后端 ToolCall (agent.go 定义) · 字段名跟前端 ToolCallRecord 不一致, 下面会映射
 *  · duration_ms / duration · 任一存在则映射到前端 durationMs (用于历史消息"已思考 X 秒"统计)
 *    历史灰度期可能两个字段名都出现 · 都兼容 · 都没有时退化为"已完成思考"无秒数 */
interface BackendToolCall {
  id: string
  name: string
  args: string
  result: string
  err?: string
  duration_ms?: number
  duration?: number
}

interface HistoryResponse {
  conversation_id: string
  messages: BackendChatMessage[]
  /** 是否还有更早的消息可翻页 · 阶段 1 不消费, 阶段 4 分页时启用 */
  has_more?: boolean
  /** 下批游标 = 本批最早一条的 id · 阶段 1 不消费, 阶段 4 分页时启用 */
  next_before_id?: number
}

// ============================================================
// 接口调用
// ============================================================

export interface FetchHistoryOptions {
  /** 默认 100 一次拉够多 (上限 100); 分页时由调用方控制 */
  limit?: number
  /** 0 = 拉最新一批; > 0 = 上批 next_before_id, 拉更早一批 */
  beforeId?: number
  signal?: AbortSignal
}

export async function fetchAiChatHistory(
  conversationId: string,
  groupID: string,
  options: FetchHistoryOptions = {}
): Promise<HistoryResponse> {
  const url = __API_SERVER_HOST__ + '/web/workgroup/folder/ai/chat/history'
  const body: HistoryRequest = {
    conversation_id: conversationId,
    group_id: groupID,
    limit: options.limit ?? 100,
    before_id: options.beforeId ?? 0
  }
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options.signal
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`)
  }
  const json = await res.json()
  return json && json.data !== undefined ? json.data : json
}

/**
 * 过滤掉协议约定的"不可渲染"消息 · 调用方 dispatch / find 前都该先跑一次
 *
 * 过滤规则:
 *   data == null · 文档 FAQ Q2: 预览操作占位 / 建夹完成回执 / 迁移前老消息
 *   业务上不展示, 直接跳过。这样下游 transformBackendMessage 可以强假设 data 非空。
 *
 * 注: data.type === 'task' (建夹任务回执) 现在会被映射为独立的 build_task_status
 * message (见 transformBackendMessage 的 task 分支), 跟会话流时序对齐, 不再过滤。
 */
export function filterRenderableHistoryMessages(
  messages: BackendChatMessage[]
): BackendChatMessage[] {
  return messages.filter((m) => m.data != null)
}

/**
 * 按 data.card.task_id 去重 data.type==='task' 的历史消息。
 *
 * 背景: 同一个 build_task 在后端会落多行 task 消息 (LLM agent 决策行 user_query=
 * "创建吧" + 任务事件回执行 user_query=""), 二者都是 data.type='task' 且共享同
 * 一 data.card.task_id (BuildTaskDetail 快照)。前端如果每行都映射成一条
 * build_task_status message, UI 会出现重复的"已为你创建 N 个智能文件夹"卡。
 *
 * 折叠策略:
 *   · 同一 task_id 多行 → 保留第一条 (按时序最早的, 即 user_query="创建吧" 那
 *     行, 让"用户说创建吧 + 任务状态"按时序贴在一起)
 *   · task_id 缺失 (老数据 / 异常) → 透传 (退化为按 message_id 单独渲染)
 *
 * 不动 non-task 消息, 顺序保持稳定。
 */
export function dedupTaskMessagesByTaskId(
  messages: BackendChatMessage[]
): BackendChatMessage[] {
  const seenTaskIds = new Set<string>()
  const result: BackendChatMessage[] = []
  for (const m of messages) {
    if (m.data?.type === 'task') {
      const taskId = (m.data.card as { task_id?: string } | undefined)?.task_id
      if (taskId) {
        if (seenTaskIds.has(taskId)) continue
        seenTaskIds.add(taskId)
      }
    }
    result.push(m)
  }
  return result
}

// ============================================================
// 后端 → 前端 message 转换
// ============================================================

/** 把后端单行消息拆成「user TextMessage + assistant AssistantMessage」两条 */
export interface FrontendMessageTuple {
  user: TextMessage
  assistant: AssistantMessage
}

/**
 * 转换单条 history message 为前端 user + assistant 双气泡
 *
 * 前置约定: 调用方已 pre-filter 掉 data == null 的消息 (见 filterRenderableHistoryMessages)。
 */
export function transformBackendMessage(
  m: BackendChatMessage
): FrontendMessageTuple {
  const ts = new Date(m.created_at).getTime() || Date.now()
  // message_id (新协议推荐) 与 id (旧兼容字段) 服务端保证同值, 这里取 message_id 优先
  const backendMessageId = m.message_id ?? m.id

  const user: TextMessage = {
    id: `hist-user-${backendMessageId}`,
    kind: 'text',
    role: 'user',
    content: m.user_query || '',
    createdAt: ts
  }

  // 后端 status 取值 (model/chat.go MessageStatus*):
  //   0 Deleted     · history 接口已过滤
  //   1 OK          · 正常 done 消息
  //   2 Failed      · 真实错误, 渲染 ERROR 卡
  //   3 Canceled    · history 接口已过滤
  //   4 InProgress  · agent 后台还在跑, 前端按 streaming 状态渲染 (豆包式接上)
  //   5 Hidden      · history 接口已过滤
  // 文档接口 4 说"只会看到 status=1", 但前端继续按取值兼容防御 (Q5 后续决策再清理)。
  const isError = m.status === 2
  const isInProgress = m.status === 4

  const { text, payload } = buildAssistantContent(m)

  const assistant: AssistantMessage = {
    id: `hist-asst-${backendMessageId}`,
    kind: 'assistant',
    role: 'ai',
    status: isInProgress ? 'streaming' : isError ? 'error' : 'done',
    text,
    createdAt: ts + 1, // 比 user 晚 1ms 保证顺序
    // 错误文本直接复用 data.content 提取出的 text (status=2 时 data 一般是 type='text' 失败回执)
    errorMessage: isError ? text : undefined,
    payload,
    // 反馈接口要的 message_id · 历史消息直接落 backendMessageId
    backendMessageId
  }
  return { user, assistant }
}

interface AssistantContent {
  text: string
  payload: AssistantPayload | undefined
}

/**
 * 构造 AssistantMessage 的 text + payload · 按 data.type 4 分流
 *
 * 调用方保证 m.data 非空 (经过 filterRenderableHistoryMessages 过滤)。
 */
function buildAssistantContent(m: BackendChatMessage): AssistantContent {
  // data 非空由调用方保证 (filterRenderableHistoryMessages), 这里强断言取值
  const data = m.data as BackendHistoryMessageData
  const toolCalls = buildToolCalls(m)

  switch (data.type) {
    case 'text':
      return {
        text: data.content || '',
        payload: toolCalls ? { action: 'final_proposal', toolCalls } : undefined
      }

    case 'multi_select':
      return buildMultiSelectContent(data, toolCalls, m.classify_state)

    case 'folder':
      return buildFolderContent(data, toolCalls, m.classify_state, m)

    case 'task':
      return buildTaskStatusContent(data, m)
  }
}

/**
 * task 类历史消息 → 独立的 build_task_status assistant message。
 *
 * 后端协议: data.type='task' 的 data.card 含 build_task 表实时快照
 * (含 task_id / status / done / total / 等 BuildTaskDetail 字段子集)。
 *
 * 映射规则:
 *   · taskId = data.card.task_id (后端已确认稳定下发, 多行 task 消息靠它去重)
 *   · 运行态 / 实际建夹数 → 不在写入时记下, 由 BuildTaskStatusMessage 组件内
 *     useBuildTaskDetail 调 /ai/chat/task 拉真实详情 (top_folders 才是任务级
 *     真相, 不靠 data.top_folders 或 folder 消息的"规划全集"推算)
 *
 * 兜底: card.task_id 缺失 (老数据 / 异常) → 退化为 message_id 作伪 taskId,
 * 组件内拉接口会失败, useBuildTaskDetail 累计错误后退出 loading, UI 静默不挂卡。
 *
 * 不返回 text · 状态卡自带文案, 避免上方再多渲染一条空 content。
 */
function buildTaskStatusContent(
  data: BackendHistoryDataTask,
  m: BackendChatMessage
): AssistantContent {
  const cardSnapshot = (data.card ?? {}) as {
    task_id?: string
    status?: string
  }
  const taskId = cardSnapshot.task_id ?? String(m.message_id ?? m.id)
  // status='done' 时 UI 跳过 "正在创建文件夹" 占位, 渲染完成态骨架。其他态
  // (running / pending / failed / canceled) 走通用 loading 路径 — failed /
  // canceled 历史场景本期不渲染 (组件内 detail.status 终态分支 return null)。
  const initialStatus: 'done' | undefined =
    cardSnapshot.status === 'done' ? 'done' : undefined
  return {
    text: '',
    payload: {
      action: 'build_task_status',
      card: {
        kind: 'build_task_status',
        taskId,
        ...(initialStatus ? { initialStatus } : {})
      }
    }
  }
}

/** multi_select → 映射到 next_level 卡 (前端已有渲染逻辑) */
function buildMultiSelectContent(
  data: BackendHistoryDataMultiSelect,
  toolCalls: ToolCallRecord[] | undefined,
  classifyState: string
): AssistantContent {
  const rawOptions = Array.isArray(data.interaction_state?.options)
    ? data.interaction_state.options
    : []
  // 后端 { key, name, detail? } → 前端 ChoiceOption { id, label, detail? }
  // detail 是副标题文案 (如选项的解释 / 数量提示), 渲染在选项 name 下方,
  // 缺失会丢失重要交互信息。
  const options: ChoiceOption[] = rawOptions.map((o) => ({
    id: o.key,
    label: o.name,
    ...(typeof o.detail === 'string' && o.detail ? { detail: o.detail } : {})
  }))
  // data.content 是 LLM 给用户的提问文本 (如 "请选一种细分粒度"), 必须写到
  // card.question 让 ChoiceOptionsCard 渲染。
  //
  // 不能写到 message.text 字段: MessageAssistant 渲染规则 "text 非空 + 有 card
  // 不渲染 text" 会把它吞掉 — 这条规则是为了避免 LLM 把"内心独白"显示在卡片
  // 上方而设的。把人话写到 card.question 才是 live / history 一致的姿势。
  const card: AssistantCard = {
    kind: 'next_level',
    question: data.content || '',
    options
  }
  return {
    text: '',
    payload: {
      action: 'next_level',
      card,
      toolCalls,
      classifyState: classifyState || undefined
    }
  }
}

/**
 * folder 类构造 · 同步阶段先用空 folders 占位
 *
 * 新协议 data.type='folder' 只带 type + content, folders / excludedKeys 不在里面;
 * 由调用方 (conversationRestore.hydrateFolderMessages) 在同步 dispatch 完所有消息后,
 * 并发跑接口 5 /history/message/folders 拉真实数据, 通过 assistantFoldersHydrated
 * action 精准回填到 card.folders / card.excludedKeys。
 *
 * 这里同步阶段保持 transformBackendMessage 签名简单 (不引入 async), 异步二次拉取
 * 由编排层负责。
 *
 * 走统一 kind 'folder_plan' (v6 重构, 跟 live 路径同构, 不再分 history/live);
 * MessageAssistant 看到本 kind 渲染 <FolderPlanPreview card /> 通用模式 ·
 * 视觉跟实时模式打平 (维度链摘要 + 文件夹树)。layersSummary/expandedSize/
 * layerCount history 路径不下发, 保持 undefined; folders/excludedKeys/
 * taskCreated 留给 hydrate 路径补齐。
 */
function buildFolderContent(
  data: BackendHistoryDataFolder,
  toolCalls: ToolCallRecord[] | undefined,
  classifyState: string,
  m: BackendChatMessage
): AssistantContent {
  // sessionId 在 history 老数据可能缺失 → 填空串, 上层用 !!sessionId 判断是否
  // 可 confirm (空 sessionId = 老数据 / 数据残缺, 仅可只读浏览)
  const sessionId = m.session_id || ''
  const card: AssistantCard = {
    kind: 'folder_plan',
    sessionId,
    chainVersion: 0, // history 不下发, 0 占位 (chainVersion 仅 live 用于过时判断)
    summary: data.content || undefined
    // folders / excludedKeys / taskCreated 由 hydrateFolderMessages 异步回填
    // layersSummary / expandedSize / layerCount history 接口不返回, 保持 undefined
  }
  return {
    text: data.content || '',
    payload: {
      // action 沿用 final_proposal 兼容 AssistantAction union; 实际渲染
      // 走 card.kind='folder_plan' 分支, 与 action 解耦
      action: 'final_proposal',
      card,
      toolCalls,
      classifyState: classifyState || undefined
    }
  }
}

function buildToolCalls(m: BackendChatMessage): ToolCallRecord[] | undefined {
  return Array.isArray(m.mcp_tool_calls)
    ? m.mcp_tool_calls.map(mapToolCall)
    : undefined
}

/** 后端 ToolCall 字段映射 → 前端 ToolCallRecord
 *  duration 兼容: 先看 duration_ms, 再看 duration, 都没有则不填
 *  paramsLabel / resultSummary 走与实时 SSE 同一套 summarize 函数,
 *  把 JSON 原文 (如 {"title_distribution":[...]}) 提炼为 "5 张 / 3 个字段命中"
 *  这类短摘要; 否则历史会话的工具行会被几十字符 JSON 撑爆时间轴布局
 *  (.toolItemRight white-space:nowrap → 右列拉长 → 左列被压成单字一列)。 */
function mapToolCall(t: BackendToolCall): ToolCallRecord {
  const durationMs =
    typeof t.duration_ms === 'number'
      ? t.duration_ms
      : typeof t.duration === 'number'
        ? t.duration
        : undefined
  return {
    id: t.id,
    toolName: t.name,
    paramsLabel: t.args ? summarizeArgs(t.args) : undefined,
    resultSummary: summarizeResult(t.result, !t.err),
    ok: !t.err,
    durationMs
  }
}
